import React from 'react';
import { Box, HStack, Skeleton, Spacer, VStack } from 'native-base';
import {
    faAngleDown,
    faAngleUp,
    faGift,
    faTrophy,
} from '@fortawesome/free-solid-svg-icons';
import { MilestoneDTO, i18nFn, useAppSettings, useSpringBigStore } from '@springbig/shared';
import Image from '~/shared/Image';
import SliderBar from '~/shared/RangeBar';
import { Caption, H3 } from '~/shared/Typography';
import { SpIcon } from '~/shared/Icon';
import { useGeneralStyles, useMilestonesStyles } from '~/shared/styles';
import Pressable from '~/shared/Pressable';

type CardMilestoneProps = {
    currentValue?: number;
    milestone: MilestoneDTO;
    t: i18nFn;
    type: 'spend' | 'points' | 'visits';
};

const CardMilestone = ({
    currentValue,
    milestone,
    t,
    type,
}: CardMilestoneProps) => {

    const [openMilestone, setOpenMilestone] = React.useState<boolean>(false);
    const {
        text,
        icon
    } = useMilestonesStyles();
    const { section_background } = useGeneralStyles();
    const { merchant } = useSpringBigStore();
    const { milestone_points_opacity } = useAppSettings();

    const getRewardNames = (): string => {

        return milestone?.rewards.map(reward => reward.item_name?.replace(/\| /g, '\n')).join(', ');
    };

    return (
        <Box p={4}>
            <Pressable
                onPress={() => {
                    if (milestone?.rewards?.length) {
                        setOpenMilestone(!openMilestone);
                    }
                }}
            >
                <Box flexDirection={'row'} justifyContent={'space-between'} w={'80%'}>
                    <Box flexDirection={'row'} mb={2} alignItems={'center'}>
                        <Caption color={text}>
                            { milestone?.name }{' '}
                        </Caption>
                        { currentValue >= milestone.value &&
                            <SpIcon size={14} color={icon} icon={faTrophy} />
                        }
                    </Box>
                    <Caption
                        color={text}
                        fontSize={14}
                        opacity={milestone_points_opacity ? milestone_points_opacity : 0.5}
                    >
                        { currentValue <= milestone.value ? currentValue : milestone.value }/{ milestone.value }
                    </Caption>
                </Box>
                <Box flexDirection={'row'} w={'100%'}>
                    <SliderBar defaultValue={currentValue} maxValue={milestone.value} w={'85%'} />
                    { milestone.rewards.length > 0 &&
                        <Pressable
                            w={'15%'}
                            flexDirection={'row'}
                            justifyContent='flex-end'
                            onPress={() => setOpenMilestone(!openMilestone)}
                        >
                            { openMilestone ?
                                <SpIcon color={icon} icon={faAngleUp} />
                                :
                                <SpIcon color={icon} icon={faAngleDown} />
                            }

                        </Pressable>
                    }
                </Box>
                { milestone?.achieved ?
                    <Caption
                        fontSize={12}
                        color={text}
                        bold
                    >{
                            milestone.rewards.length > 0 ?
                                t('milestones.achieved', [getRewardNames()])
                                :
                                t('milestones.milestone_achieved')
                        }</Caption>
                    :
                    <Caption
                        color={text}
                        fontSize={12}
                        bold
                    >{
                            type === 'spend' ? (
                                t('milestones.spends_title', [milestone.remaining, merchant?.name, getRewardNames()])
                            ) : (
                                t('milestones.points_title', [milestone.remaining, merchant?.name, getRewardNames()])
                            )
                        }</Caption>
                }
                { openMilestone &&
                    <Box w={'100%'} >
                        <H3
                            color={text}
                            marginY={2}
                        >
                            {
                                milestone?.achieved ? (
                                    t('milestones.congratulations', [milestone?.name])
                                ) : (
                                    type === 'spend' ? (
                                        t('milestones.spends_remaining', [milestone.remaining, getRewardNames()])
                                    ) : (
                                        t('milestones.points_remaining', [milestone.remaining, getRewardNames()])
                                    )
                                )
                            }
                        </H3>

                        <Box>
                            <HStack space={2} mt={2}>
                                <SpIcon
                                    color={icon}
                                    icon={faGift}
                                />
                                <Caption
                                    color={text}
                                    fontWeight={'semibold'}
                                    text='milestones.benefits_title'
                                />
                            </HStack>
                            { milestone?.rewards?.length && (
                                <Box mb={2} mt={2}>
                                    { milestone?.rewards?.map((reward, index) =>

                                        <Box
                                            key={index}
                                            flexDirection={'row'}
                                            justifyContent={'space-between'}
                                            alignItems={'center'}
                                        >
                                            <Caption color={text} pl={6} bold>
                                                { reward?.item_name?.replace(/\| /g, '\n') }
                                            </Caption>
                                            { reward.preload ?
                                                <Image
                                                    w={12}
                                                    h={12}
                                                    borderRadius={8}
                                                    uri={`${reward.preload}`}
                                                />
                                                :
                                                <SpIcon color={icon} icon={faGift} size={40} />
                                            }
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                }
            </Pressable>
        </Box>
    );
};

export const CardMilestoneSkeleton = () => {

    const gStyle = useGeneralStyles();

    return (
        <VStack space={4} backgroundColor={'gray'} p={5} pr={'7rem'} bg={gStyle.section_background}>
            <HStack>
                <Skeleton h={2} rounded={'md'} flex={1} />
                <Spacer flex={1} />
                <Skeleton h={2} rounded={'md'} flex={1} />
            </HStack>
            <Skeleton h={2} rounded={'md'} w='100%' />
            <Skeleton width={'40%'} h={2} rounded={'md'} />

        </VStack>
    );
};

export default CardMilestone;
